import { isAString, isInputAnArray } from "utils/helpers.utils";
import type { ProductImageBadge } from "./express.product.types";
import type { Product } from "mocks/SharedTransform/interface";
import { ServiceType } from "mocks/SharedTransform/interface";
import AppConstants from "constants/AppConstants";

// ============================================================================
// Constants
// ============================================================================

/**
 * Cached normalized fresh-cut service type for performance optimization.
 */
const FRESH_CUT_NORMALIZED = ServiceType.FRESH_CUT.toLowerCase();

// ============================================================================
// Private Helper Functions
// ============================================================================

/**
 * Retrieves virtual products from a product.
 * Returns an empty array if no virtual products exist.
 *
 * @param product - The product to extract virtual products from
 * @returns Array of virtual products or empty array
 */
function getVirtualProducts(
  product: Product,
): NonNullable<Product['virtualProducts']> {
  if (
    !product?.virtualProducts ||
    !isInputAnArray(product.virtualProducts) ||
    product.virtualProducts.length === 0
  ) {
    return [];
  }

  return product.virtualProducts;
}

/**
 * Normalizes a service type value for case-insensitive comparison.
 *
 * @param serviceType - The service type to normalize
 * @returns Lowercase service type string or empty string
 */
function normalizeServiceType(serviceType?: ServiceType | string): string {
  return serviceType && isAString(serviceType) 
    ? serviceType.toLowerCase() 
    : '';
}

/**
 * Determines if a given service type is fresh-cut.
 *
 * @param serviceType - The service type to check
 * @returns True if the service type is fresh-cut
 */
export function isFreshCutServiceType(
  serviceType?: ServiceType | string,
): boolean {
  const normalizedServiceType = normalizeServiceType(serviceType);
  return normalizedServiceType === FRESH_CUT_NORMALIZED;
}

/**
 * Checks whether a product is eligible for the fresh-cut badge.
 * A product is eligible if it has at least one fresh-cut virtual product.
 *
 * @param product - The product to check
 * @returns True if fresh-cut badge should be displayed
 */
function isFreshCutBadgeEligible(product: Product): boolean {
  return getVirtualProducts(product).some((virtualProduct) =>
    isFreshCutServiceType(virtualProduct.serviceType),
  );
}

/**
 * Creates a fresh-cut badge object for product display.
 *
 * @returns Fresh-cut badge configuration
 */
export function getFreshCutBadge(): ProductImageBadge {
  return {
    label: AppConstants.FRESH_CUT_BADGE_LABEL,
  };
}

/**
 * Checks whether a product has at least one virtual product.
 *
 * @param product - The product to check
 * @returns True if product has virtual products
 */
export function hasVirtualProducts(product: Product): boolean {
  return getVirtualProducts(product).length > 0;
}

/**
 * Retrieves the localized name for a virtual product.
 * Prioritizes English, falls back to Arabic.
 *
 * @param virtualProduct - The virtual product to get name from
 * @returns Localized product name or undefined if not available
 */
export function getLocalizedVirtualProductName(
  virtualProduct: NonNullable<Product['virtualProducts']>[number],
): string | undefined {
  return virtualProduct.name?.en || virtualProduct.name?.ar;
}

/**
 * Generates a joined label string from all virtual product names.
 *
 * @param product - The product to extract virtual product labels from
 * @returns Joined virtual product names separated by ' / ', or undefined if none exist
 */
export function getVirtualProductInfoLabel(
  product: Product,
): string | undefined {
  const virtualProductNames = getVirtualProducts(product)
    .map(getLocalizedVirtualProductName)
    .filter((name): name is string => !!name);

  if (virtualProductNames.length === 0) {
    return undefined;
  }

  return virtualProductNames.join(' / ');
}

/**
 * Retrieves complete virtual product display information for a product.
 * Includes badge eligibility, badge configuration, and virtual product labels.
 *
 * @param product - The product to get display information for
 * @returns Object containing badge status, badge configuration, and virtual product label
 */
export function getVirtualProductDisplayInfo(
  product: Product,
): {
  hasBadge: boolean;
  badge?: ProductImageBadge;
  virtualProductLabel?: string;
} {
  const hasBadge = isFreshCutBadgeEligible(product);
  const badge = hasBadge ? getFreshCutBadge() : undefined;
  const virtualProductLabel = getVirtualProductInfoLabel(product);

  return {
    hasBadge,
    badge,
    virtualProductLabel,
  };
}
